import type {
    CacheControlMetadata,
    CacheEntry,
    Key,
    FilterPredicate,
} from '@conduit-client/service-cache/v1';

interface LogicalOperators<T> {
    $and?: T[];
    $or?: T[];
    $not?: T;
}

export type CacheKeyQueryOperands = { $regex: RegExp };

export type CacheMetadataQueryOperands =
    | LogicalOperators<CacheMetadataQueryOperands>
    | { cacheControlType: { $eq: 'max-age' | 'stale-while-revalidate' | 'no-cache' | 'no-store' } }
    | { maxAge: { $gte?: number; $lte?: number } };

export type CacheValueQueryOperands = never; // TODO: Implement value-based querying

export type CacheQuery =
    | LogicalOperators<CacheQuery>
    | { key: CacheKeyQueryOperands }
    | { metadata: CacheMetadataQueryOperands }
    | { value: CacheValueQueryOperands };

export const isAndQuery = (query: CacheQuery): query is { $and: CacheQuery[] } => '$and' in query;

export const isOrQuery = (query: CacheQuery): query is { $or: CacheQuery[] } => '$or' in query;

export const isNotQuery = (query: CacheQuery): query is { $not: CacheQuery } => '$not' in query;

export const matchesMetadata = (
    metadataQuery: CacheMetadataQueryOperands,
    cacheControl: CacheControlMetadata
): boolean => {
    if (
        'cacheControlType' in metadataQuery &&
        cacheControl.type !== metadataQuery.cacheControlType.$eq
    ) {
        return false;
    }
    if ('maxAge' in metadataQuery && cacheControl.type === 'max-age') {
        const maxAge = cacheControl.maxAge ?? 0;
        if (
            (metadataQuery.maxAge.$gte !== undefined && maxAge < metadataQuery.maxAge.$gte) ||
            (metadataQuery.maxAge.$lte !== undefined && maxAge > metadataQuery.maxAge.$lte)
        ) {
            return false;
        }
    }
    return true;
};
export function queryToPredicate(query: CacheQuery): FilterPredicate {
    return (key: Key, entry: CacheEntry<unknown>): boolean => {
        if (!query) return false;

        if (isAndQuery(query))
            return query.$and.every((subQuery) => queryToPredicate(subQuery)(key, entry));
        if (isOrQuery(query))
            return query.$or.some((subQuery) => queryToPredicate(subQuery)(key, entry));
        if (isNotQuery(query)) return !queryToPredicate(query.$not)(key, entry);

        if ('key' in query) return matchesKey(query.key, key);
        if ('metadata' in query)
            return matchesMetadata(query.metadata, entry.metadata.cacheControl);

        if ('value' in query) return false; // TODO: Not implemented

        throw new Error('Unknown Query Operation');
    };
}
function matchesKey(keyQuery: CacheKeyQueryOperands, key: Key): boolean {
    if ('$regex' in keyQuery) {
        return keyQuery.$regex.test(key);
    }
    return false;
}
